import { HttpResponse } from '@angular/common/http';
import { HttpParams } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { EnumSiNoTodos } from 'src/app/components/core/_infrastructure/types/common';
import { FilterSearchingTWRecintoViewModel, RecintoItemResultsViewModel, TwRecintoViewModel } from './recinto.types';
import { RecintoInfoRequestViewModel } from 'src/app/components/_exportacion/atestaciones/lugarproduccion/lugarproduccion.types';
import { ResetFiltersService } from '../../core/_infrastructure/classes/services/reset-filters.service';
import { AuthService } from '../../core/_infrastructure/classes/services/auth.service';

@Injectable()
export class RecintoService extends ResetFiltersService {
    private recintoEndpoint = this.baseUrl + 'api/campanias/recinto/';

    private arrBooleanKeys = ['EsCompleta', 'Autorizado'];
    private lastSearch?: FilterSearchingTWRecintoViewModel;

    constructor(
        protected authService: AuthService,
        private http: HttpClient,
        @Inject('BASE_URL') private baseUrl: string,
    ) {
        super(authService);
    }

    /**
     * Creates an HttpParms object through the values in the parameter.
     * @param payload FilterSearchingTWRecintoViewModel
     * @returns HttpParams
     */
    private convertModelToHttpParms(payload: FilterSearchingTWRecintoViewModel): HttpParams {
        let params = new HttpParams();
        let k: keyof FilterSearchingTWRecintoViewModel;

        for (k in payload) {
            let v;
            let valor = payload[k];
            if (this.arrBooleanKeys.indexOf(k) !== -1 && valor == EnumSiNoTodos.NO) {
                v = 0;
            } else {
                v = payload[k] || '';
            }
            params = params.append(k, v);
        }

        return params;
    }

    /**
     * Invokes the service to get the list of "Recintos", applying the filter in the parameter
     * @param payload FilterSearchingTWRecintoViewModel
     * @returns Observable<RecintoItemResultsViewModel>
     */
    getRecintos(payload: FilterSearchingTWRecintoViewModel): Observable<RecintoItemResultsViewModel> {
        this.lastSearch = { ...payload }; // Keep for future search
        const params = this.convertModelToHttpParms(payload);
        // @author: Aunque se envían IdPeriodo e IdCampania, en éste método del core se descartan, pues no se necesitan. Se mantienen por compatibilidad de tipos con la consulta de cultivos.
        return this.getRecintosPagedByFilter(params);
    }

    /**
     * Invokes the service to get the list of "Recintos", applying the filter in the parameter
     * @param params HttpParams
     * @returns Observable<RecintoItemResultsViewModel>
     */
    private getRecintosPagedByFilter(params: HttpParams): Observable<RecintoItemResultsViewModel> {
        return this.http.get<RecintoItemResultsViewModel>(this.recintoEndpoint + 'PagedByFilter/', { params: params });
    }

    getRecinto(id: number): Observable<TwRecintoViewModel> {
        return this.http.get<TwRecintoViewModel>(this.recintoEndpoint + id);
    }

    getRecintosByIdProvincia(idProvincia: number): Observable<TwRecintoViewModel[]> {
        return this.http.get<TwRecintoViewModel[]>(this.recintoEndpoint + 'RecintosByIdProvincia/' + idProvincia);
    }

    saveRecinto(recinto: TwRecintoViewModel): Observable<any> {
        // delete model.Descripcion;  // Para forzar un fallo en el backend y ver que pasa en el catchError
        if (recinto?.Id) {
            return this.editRecinto(recinto);
        }
        return this.createRecinto(recinto);
    }

    private createRecinto(recinto: TwRecintoViewModel): Observable<any> {
        // recinto.Id = 1121; // Para forzar un fallo en el backend y ver que pasa en el catchError
        return this.http.post<TwRecintoViewModel>(this.recintoEndpoint, recinto);
    }

    private editRecinto(recinto: TwRecintoViewModel): Observable<any> {
        // recinto.Id = null; // Para forzar un fallo en el backend y ver que pasa en el catchError
        return this.http.put<TwRecintoViewModel>(this.recintoEndpoint, recinto);
    }

    export(model: FilterSearchingTWRecintoViewModel): Observable<HttpResponse<Blob>> {
        return this.http.post(this.recintoEndpoint + 'postExportExcelByFilter', model, { observe: 'response', responseType: 'blob' });
    }

    getLastSearch(): FilterSearchingTWRecintoViewModel | undefined {
        return this.lastSearch;
    }

    validarParcelaSigpac(model: RecintoInfoRequestViewModel): Observable<any> {
        return this.http.post<RecintoInfoRequestViewModel>(this.baseUrl + 'api/exportacion/atestaciones/lugarProduccionAtest/postValidarParcelaSigpac', model);
    }

    resetFilter() {
        this.lastSearch = undefined;
    }
}
